import { and, asc, eq, inArray, isNull, like, sql } from 'drizzle-orm';
import { db } from '@/db/client';
import { tags, transactions, transactionTags, recurringTransactionTags } from '@/db/schema';
import type { ProjectedRecurringTag } from '@/lib/projectedRecurringTransactions';
import { generateId } from '@/lib/id';
import { ExpectedError } from '@/lib/errorUtils';
import i18n from '@/lib/i18n';

export type Tag = typeof tags.$inferSelect;
export type TagType = Tag;

export interface TagWithUsage extends Tag {
    /** Quantas transações (não excluídas) usam a tag. */
    usageCount: number;
}

// Ordem manual primeiro (definida pelo usuário na tela de gerenciamento), nome
// como desempate. Vale em todo lugar que lista tags (picker incluso).
const tagOrdering = [asc(tags.sortOrder), asc(tags.name)] as const;

/** Próximo sort_order livre — novas tags entram no fim da lista. */
async function getNextTagSortOrder(): Promise<number> {
    const [row] = await db
        .select({ max: sql<number>`COALESCE(MAX(${tags.sortOrder}), -1)` })
        .from(tags);
    return (row?.max ?? -1) + 1;
}

export async function getTags(): Promise<Tag[]> {
    return db.select().from(tags).orderBy(...tagOrdering);
}

/**
 * Lista as tags com a contagem de transações que as usam (ignora soft-deleted).
 * Base da tela de gerenciamento de tags.
 */
export async function getTagsWithUsage(): Promise<TagWithUsage[]> {
    return db
        .select({
            id: tags.id,
            name: tags.name,
            color: tags.color,
            sortOrder: tags.sortOrder,
            createdAt: tags.createdAt,
            usageCount: sql<number>`COUNT(${transactions.id})`,
        })
        .from(tags)
        .leftJoin(transactionTags, eq(transactionTags.tagId, tags.id))
        .leftJoin(
            transactions,
            and(eq(transactions.id, transactionTags.transactionId), isNull(transactions.deletedAt)),
        )
        .groupBy(tags.id)
        .orderBy(...tagOrdering);
}

/** Persiste a ordem manual das tags (índice na lista = sort_order). */
export async function reorderTags(orderedIds: string[]): Promise<void> {
    for (let i = 0; i < orderedIds.length; i++) {
        await db.update(tags).set({ sortOrder: i }).where(eq(tags.id, orderedIds[i]));
    }
}

/**
 * Cria uma tag explicitamente (com cor). Diferente de getOrCreateTag, falha se o
 * nome já existir — usado pela tela de gerenciamento, onde criar é uma ação clara.
 */
export async function createTag(name: string, color?: string): Promise<Tag> {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) {
        throw new ExpectedError(i18n.t('ui:errors.tagNameEmpty'));
    }
    const existing = await db
        .select({ id: tags.id })
        .from(tags)
        .where(eq(tags.name, trimmed));
    if (existing[0]) {
        throw new ExpectedError(i18n.t('ui:errors.tagNameTaken'));
    }

    const id = generateId();
    await db.insert(tags).values({
        id,
        name: trimmed,
        color: color ?? '#6B7280',
        sortOrder: await getNextTagSortOrder(),
        createdAt: new Date().toISOString(),
    });

    const result = await db.select().from(tags).where(eq(tags.id, id));
    return result[0];
}

/**
 * Renomeia e/ou recolore uma tag. Normaliza o nome (trim + lowercase, como na
 * criação) e impede colidir com outra tag existente.
 */
export async function updateTag(
    id: string,
    data: { name?: string; color?: string },
): Promise<void> {
    const patch: { name?: string; color?: string } = {};

    if (data.name !== undefined) {
        const trimmed = data.name.trim().toLowerCase();
        if (!trimmed) {
            throw new ExpectedError(i18n.t('ui:errors.tagNameEmpty'));
        }
        const existing = await db
            .select({ id: tags.id })
            .from(tags)
            .where(eq(tags.name, trimmed));
        if (existing[0] && existing[0].id !== id) {
            throw new ExpectedError(i18n.t('ui:errors.tagNameTaken'));
        }
        patch.name = trimmed;
    }

    if (data.color !== undefined) {
        patch.color = data.color;
    }

    if (Object.keys(patch).length === 0) return;

    await db.update(tags).set(patch).where(eq(tags.id, id));
}

/**
 * Exclui a tag definitivamente. Remove primeiro os vínculos em transaction_tags
 * (FK) — as transações em si são preservadas, só perdem essa tag.
 */
export async function deleteTag(id: string): Promise<void> {
    await db.delete(transactionTags).where(eq(transactionTags.tagId, id));
    await db.delete(recurringTransactionTags).where(eq(recurringTransactionTags.tagId, id));
    await db.delete(tags).where(eq(tags.id, id));
}

export async function searchTags(query: string): Promise<Tag[]> {
    if (!query) return getTags();
    return db
        .select()
        .from(tags)
        .where(like(tags.name, `%${query}%`))
        .orderBy(...tagOrdering);
}

export async function getOrCreateTag(name: string): Promise<Tag> {
    const trimmed = name.trim().toLowerCase();
    const existing = await db
        .select()
        .from(tags)
        .where(eq(tags.name, trimmed));

    if (existing[0]) return existing[0];

    const id = generateId();
    await db.insert(tags).values({
        id,
        name: trimmed,
        sortOrder: await getNextTagSortOrder(),
        createdAt: new Date().toISOString(),
    });

    const result = await db.select().from(tags).where(eq(tags.id, id));
    return result[0];
}

export async function getTagsForTransaction(transactionId: string): Promise<Tag[]> {
    const rows = await db
        .select({ id: tags.id, name: tags.name, color: tags.color, sortOrder: tags.sortOrder, createdAt: tags.createdAt })
        .from(transactionTags)
        .innerJoin(tags, eq(transactionTags.tagId, tags.id))
        .where(eq(transactionTags.transactionId, transactionId));
    return rows;
}

/** Tags de várias transações de uma vez (chunked pra não estourar o limite de IN do SQLite). */
export async function getTagsForTransactionIds(transactionIds: string[]): Promise<Map<string, Tag[]>> {
    const tagsByTransaction = new Map<string, Tag[]>();
    if (transactionIds.length === 0) return tagsByTransaction;

    const CHUNK = 400;
    for (let i = 0; i < transactionIds.length; i += CHUNK) {
        const rows = await db
            .select({
                transactionId: transactionTags.transactionId,
                id: tags.id,
                name: tags.name,
                color: tags.color,
                sortOrder: tags.sortOrder,
                createdAt: tags.createdAt,
            })
            .from(transactionTags)
            .innerJoin(tags, eq(transactionTags.tagId, tags.id))
            .where(inArray(transactionTags.transactionId, transactionIds.slice(i, i + CHUNK)))
            .orderBy(...tagOrdering);
        for (const row of rows) {
            const list = tagsByTransaction.get(row.transactionId) ?? [];
            list.push({ id: row.id, name: row.name, color: row.color, sortOrder: row.sortOrder, createdAt: row.createdAt });
            tagsByTransaction.set(row.transactionId, list);
        }
    }
    return tagsByTransaction;
}

/**
 * Dos ids pedidos (sem repetição, na ordem pedida), só os de tags que ainda existem.
 * Tag é DELETE físico: um id que sumiu entre carregar a tela e salvar estoura FOREIGN KEY
 * no insert do vínculo (Sentry REACT-NATIVE-7, 1.0.67).
 */
async function existingTagIds(tagIds: string[]): Promise<string[]> {
    const uniqueTagIds = [...new Set(tagIds)];
    if (uniqueTagIds.length === 0) return [];
    const rows = await db.select({ id: tags.id }).from(tags).where(inArray(tags.id, uniqueTagIds));
    const alive = new Set(rows.map((row) => row.id));
    return uniqueTagIds.filter((tagId) => alive.has(tagId));
}

export async function setTransactionTags(transactionId: string, tagIds: string[]): Promise<void> {
    // Valida ANTES do delete: com o insert estourando depois dele, o lançamento perdia
    // todas as tags, inclusive as válidas.
    const validTagIds = await existingTagIds(tagIds);

    // Delete existing
    await db.delete(transactionTags).where(eq(transactionTags.transactionId, transactionId));

    // Insert new (dedupe: o par (transação, tag) tem índice único — ver migração 0019).
    if (validTagIds.length > 0) {
        await db.insert(transactionTags).values(
            validTagIds.map((tagId) => ({ transactionId, tagId })),
        );
    }
}

/**
 * Substitui o conjunto de tags das parcelas de um parcelamento conforme o escopo
 * do seletor de edição ('single' = só a âncora, 'future' = âncora e seguintes,
 * 'all' = série inteira). Espelha o desenho da recorrência (setRecurringSeriesTags):
 * sem ele, editar uma parcela com "todas"/"futuras" gravava a tag só na âncora.
 */
export async function setInstallmentSeriesTags(params: {
    installmentId: string;
    anchorInstallmentNumber: number;
    mode: import('@/lib/seriesRebase').EditScope;
    tagIds: string[];
}): Promise<void> {
    const { installmentId, anchorInstallmentNumber, mode, tagIds } = params;
    const rows = await db
        .select({ id: transactions.id, installmentNumber: transactions.installmentNumber })
        .from(transactions)
        .where(and(eq(transactions.installmentId, installmentId), isNull(transactions.deletedAt)));

    const inScope = rows.filter((row) => {
        const number = row.installmentNumber ?? anchorInstallmentNumber;
        if (mode === 'all') return true;
        if (mode === 'future') return number >= anchorInstallmentNumber;
        return number === anchorInstallmentNumber;
    });

    for (const row of inScope) {
        await setTransactionTags(row.id, tagIds);
    }
}

/** Tags da REGRA recorrente (série) — fonte de verdade que as ocorrências herdam. */
export async function getTagsForRecurring(recurringId: string): Promise<Tag[]> {
    const rows = await db
        .select({ id: tags.id, name: tags.name, color: tags.color, sortOrder: tags.sortOrder, createdAt: tags.createdAt })
        .from(recurringTransactionTags)
        .innerJoin(tags, eq(recurringTransactionTags.tagId, tags.id))
        .where(eq(recurringTransactionTags.recurringId, recurringId));
    return rows;
}

/**
 * Tags das REGRAS recorrentes, em lote — o que a Ocorrência projetada herda (ADR 0101).
 * Devolve a forma estreita de `TransactionTagInfo`/`ProjectedRecurringTag`, que é o que o motor
 * de projeção repassa e as telas consomem. `IN` em lotes pra não estourar o limite de variáveis
 * do SQLite, no mesmo padrão de `getTagsForTransactionIds`.
 */
export async function getTagInfoForRecurringIds(
    recurringIds: string[],
): Promise<Map<string, ProjectedRecurringTag[]>> {
    const byRecurring = new Map<string, ProjectedRecurringTag[]>();
    if (recurringIds.length === 0) return byRecurring;

    const uniqueIds = [...new Set(recurringIds)];
    const CHUNK = 400;
    for (let i = 0; i < uniqueIds.length; i += CHUNK) {
        const rows = await db
            .select({
                recurringId: recurringTransactionTags.recurringId,
                id: tags.id,
                name: tags.name,
                color: tags.color,
            })
            .from(recurringTransactionTags)
            .innerJoin(tags, eq(recurringTransactionTags.tagId, tags.id))
            .where(inArray(recurringTransactionTags.recurringId, uniqueIds.slice(i, i + CHUNK)))
            .orderBy(asc(tags.name));
        for (const row of rows) {
            const list = byRecurring.get(row.recurringId) ?? [];
            list.push({ id: row.id, name: row.name, color: row.color });
            byRecurring.set(row.recurringId, list);
        }
    }
    return byRecurring;
}

export async function setRecurringTags(recurringId: string, tagIds: string[]): Promise<void> {
    await db.delete(recurringTransactionTags).where(eq(recurringTransactionTags.recurringId, recurringId));

    // Dedupe: o par (regra, tag) tem índice único — ver migração 0019.
    const uniqueTagIds = [...new Set(tagIds)];
    if (uniqueTagIds.length > 0) {
        await db.insert(recurringTransactionTags).values(
            uniqueTagIds.map((tagId) => ({ recurringId, tagId })),
        );
    }
}

// ─── Tags de financiamentos e empréstimos pessoais ───────────
// A tag desses contratos vive nas transações que eles geram (transaction_tags,
// já coberto por backup e sync). A união considera também linhas soft-deletadas:
// cronograma/série são apagados e re-materializados pelos repositórios, e as
// junctions das linhas deletadas preservam o conjunto entre os rebuilds.

type LoanEntityColumn = typeof transactions.financingId | typeof transactions.personalLoanId;

async function getEntityTags(column: LoanEntityColumn, entityId: string): Promise<Tag[]> {
    return db
        .selectDistinct({
            id: tags.id,
            name: tags.name,
            color: tags.color,
            sortOrder: tags.sortOrder,
            createdAt: tags.createdAt,
        })
        .from(transactionTags)
        .innerJoin(tags, eq(transactionTags.tagId, tags.id))
        .innerJoin(transactions, eq(transactionTags.transactionId, transactions.id))
        .where(eq(column, entityId))
        .orderBy(...tagOrdering);
}

/**
 * Substitui as tags da entidade em TODOS os lançamentos dela — incluindo os
 * soft-deletados, senão a união de `getEntityTags` ressuscitaria uma tag
 * removida no próximo sync.
 *
 * Em LOTE, não por linha: um contrato longo tem centenas de lançamentos — são
 * 2 statements chunked no lugar de 2 por linha. E só tags VIVAS entram: um id
 * stale (tag apagada com o sheet ainda em cache) não pode virar FK violation
 * espalhada pelas parcelas.
 */
async function setEntityTags(column: LoanEntityColumn, entityId: string, tagIds: string[]): Promise<void> {
    const rows = await db
        .select({ id: transactions.id })
        .from(transactions)
        .where(eq(column, entityId));
    if (rows.length === 0) return;

    const validTagIds = await existingTagIds(tagIds);

    const CHUNK = 400;
    const transactionIds = rows.map((row) => row.id);
    for (let i = 0; i < transactionIds.length; i += CHUNK) {
        await db.delete(transactionTags).where(inArray(transactionTags.transactionId, transactionIds.slice(i, i + CHUNK)));
    }
    if (validTagIds.length > 0) {
        const pairs = transactionIds.flatMap((transactionId) =>
            validTagIds.map((tagId) => ({ transactionId, tagId })),
        );
        // Chunk por PAR (2 bind params cada) pra ficar longe do limite de
        // variáveis do SQLite mesmo com várias tags.
        for (let i = 0; i < pairs.length; i += CHUNK) {
            await db.insert(transactionTags).values(pairs.slice(i, i + CHUNK));
        }
    }
}

/**
 * Propaga a união de tags da entidade para os lançamentos VIVOS divergentes —
 * abatimento, recebimento, baixa e série/cauda regenerada nascem sem tag e
 * herdam aqui. Só escreve quando o conjunto difere (junctions entram no oplog).
 */
async function syncEntityTransactionTags(column: LoanEntityColumn, entityId: string): Promise<void> {
    const unionTagIds = (await getEntityTags(column, entityId)).map((tag) => tag.id).sort();
    if (unionTagIds.length === 0) return;

    const liveRows = await db
        .select({ id: transactions.id })
        .from(transactions)
        .where(and(eq(column, entityId), isNull(transactions.deletedAt)));
    if (liveRows.length === 0) return;

    const targetKey = unionTagIds.join('|');
    const currentByTransaction = await getTagsForTransactionIds(liveRows.map((row) => row.id));
    for (const row of liveRows) {
        const currentKey = (currentByTransaction.get(row.id) ?? [])
            .map((tag) => tag.id)
            .sort()
            .join('|');
        if (currentKey !== targetKey) {
            await setTransactionTags(row.id, unionTagIds);
        }
    }
}

export async function getFinancingTags(financingId: string): Promise<Tag[]> {
    return getEntityTags(transactions.financingId, financingId);
}

export async function setFinancingTags(financingId: string, tagIds: string[]): Promise<void> {
    return setEntityTags(transactions.financingId, financingId, tagIds);
}

export async function syncFinancingTransactionTags(financingId: string): Promise<void> {
    return syncEntityTransactionTags(transactions.financingId, financingId);
}

export async function getPersonalLoanTags(loanId: string): Promise<Tag[]> {
    return getEntityTags(transactions.personalLoanId, loanId);
}

export async function setPersonalLoanTags(loanId: string, tagIds: string[]): Promise<void> {
    return setEntityTags(transactions.personalLoanId, loanId, tagIds);
}

export async function syncPersonalLoanTransactionTags(loanId: string): Promise<void> {
    return syncEntityTransactionTags(transactions.personalLoanId, loanId);
}
